// Shared by pic-image and pic-svg: decides whether an SVG is grayscale/monochrome (safe to
// contrast-compress for dark mode) or contains actual color (a logo etc.) that must be left
// alone. Also used by pic-label to detect an explicit black color that needs to flip for dark mode.
const monoSvgCache = new Map();
let colorCtx = null;
export function isSvgSrc(src) {
    return /\.svg(?:[?#]|$)/i.test(src);
}
// Pulls every explicitly-declared fill/stroke color out of a style declaration list (a style=""
// attribute, or the body of a <style> block).
function extractDeclaredColors(cssText) {
    const colors = [];
    const re = /(?:fill|stroke)\s*:\s*([^;}"']+)/gi;
    let m;
    while ((m = re.exec(cssText)) !== null) {
        colors.push(m[1].trim());
    }
    return colors;
}
function isSkippableColor(value) {
    const v = value.trim().toLowerCase();
    return v === '' || v === 'none' || v === 'transparent' || v === 'inherit' || v === 'currentcolor' || v.startsWith('url(');
}
// Normalizes any CSS color string (hex, rgb(), named, ...) to RGB via the canvas 2D context's
// color parser, rather than re-implementing CSS color parsing here.
function colorToRGB(colorStr) {
    if (!colorCtx) {
        colorCtx = document.createElement('canvas').getContext('2d');
    }
    const ctx = colorCtx;
    if (!ctx)
        return null;
    const sentinel = 'rgb(1, 2, 3)';
    ctx.fillStyle = sentinel;
    try {
        ctx.fillStyle = colorStr;
    }
    catch (_a) {
        return null;
    }
    const normalized = ctx.fillStyle;
    if (normalized === sentinel && colorStr.trim().toLowerCase() !== sentinel) {
        return null;
    }
    const hexMatch = normalized.match(/^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i);
    if (hexMatch) {
        return [parseInt(hexMatch[1], 16), parseInt(hexMatch[2], 16), parseInt(hexMatch[3], 16)];
    }
    const rgbMatch = normalized.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
    if (rgbMatch) {
        return [parseInt(rgbMatch[1], 10), parseInt(rgbMatch[2], 10), parseInt(rgbMatch[3], 10)];
    }
    return null;
}
function isGrayscaleColor(colorStr) {
    const rgb = colorToRGB(colorStr);
    if (!rgb)
        return false;
    const [r, g, b] = rgb;
    return Math.max(r, g, b) - Math.min(r, g, b) <= 12;
}
// Recognizes black in any CSS form ('black', '#000', '#000000', 'rgb(0,0,0)', 'rgba(0,0,0,.87)', ...)
// so callers can swap it for a theme-aware color in dark mode. Alpha is ignored - a black at any
// opacity is still black. Fails safe: an unparseable color is treated as not black.
export function isBlackColor(colorStr) {
    const rgb = colorToRGB(colorStr);
    if (!rgb)
        return false;
    const [r, g, b] = rgb;
    return r === 0 && g === 0 && b === 0;
}
// Elements with no fill/stroke attribute are deliberately NOT included here: an unset fill
// defaults to black (already grayscale, so ignoring it is safe) but an unset stroke defaults to
// none/invisible (assuming it's black would be wrong).
export function isSvgTextMonochrome(svgText) {
    const doc = new DOMParser().parseFromString(svgText, 'image/svg+xml');
    if (doc.querySelector('parsererror'))
        return false;
    const candidates = [];
    doc.querySelectorAll('[fill]').forEach(el => candidates.push(el.getAttribute('fill')));
    doc.querySelectorAll('[stroke]').forEach(el => candidates.push(el.getAttribute('stroke')));
    doc.querySelectorAll('[style]').forEach(el => candidates.push(...extractDeclaredColors(el.getAttribute('style'))));
    doc.querySelectorAll('style').forEach(styleEl => candidates.push(...extractDeclaredColors(styleEl.textContent || '')));
    for (const raw of candidates) {
        if (isSkippableColor(raw))
            continue;
        if (!isGrayscaleColor(raw))
            return false;
    }
    return true;
}
// Fetches + caches (per src, shared across all callers) whether an SVG is monochrome. Fails
// safe: a network/CORS/parse error is treated as "not monochrome" so the image is left alone.
export function fetchIsSvgMonochrome(src) {
    let cached = monoSvgCache.get(src);
    if (!cached) {
        cached = fetch(src)
            .then(res => res.ok ? res.text() : Promise.reject())
            .then(text => isSvgTextMonochrome(text))
            .catch(() => false);
        monoSvgCache.set(src, cached);
    }
    return cached;
}
//# sourceMappingURL=SvgColor.js.map